<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ChevronLeftIcon, ChevronRightIcon } from '@blpde/nx-icons';
import { NxButton } from '../Button';
import { NxToggleGroup } from '../ToggleGroup';
import { i18nInjectionKey } from '../internal';
import NxCalendarMonthGrid from './NxCalendarMonthGrid.vue';
import { indexDays, isIsoDate, parseIso, shiftMonths, toIso } from './calendarGrid';
import type { MonthDensity, NxCalendarDay, NxCalendarView } from './types';

const props = withDefaults(
  defineProps<{
    /** Which grid to render. */
    view?: NxCalendarView;
    /**
     * ISO `YYYY-MM-DD` the view centres on: the year view uses only its year,
     * the month view its year and month. The consumer places its own controls
     * and may move this prop at any time; every move the component makes on its
     * own — a header step, today, the keyboard cursor leaving the month — is
     * emitted back, so `v-model:focus-date` always names the period on screen.
     */
    focusDate: string;
    /** Sparse: only decorated days. Undecorated days need no entry. */
    days?: NxCalendarDay[];
    firstDayOfWeek?: 1 | 7;
    locale?: string;
    /** Ignored in the year view — a week column costs width it cannot pay for. */
    showWeekNumbers?: boolean;
    /**
     * Makes every day a button, keyboard-reachable via a roving tabindex — and
     * it is what gates `dayClick`: a calendar that is not selectable does not
     * look clickable and does not report clicks either. A consumer that wants
     * the event without the keyboard affordance still turns this on.
     */
    selectable?: boolean;
    /**
     * The header line: navigation and today on the left, the `controls` slot in
     * the middle, the view switch on the right. Off when the consumer builds its
     * own chrome — then the month blocks carry their name again.
     */
    showHeader?: boolean;
    /**
     * Month view only, opt-in: the small month of a sidebar — compact cells,
     * adjacent days shown, the grid's own height instead of the stretched
     * floor. Without a header the month heading carries the year as well.
     */
    compact?: boolean;
    /** Opt-in: this ISO day is drawn as the selected one (a filled day number). */
    selectedDate?: string | null;
    /**
     * Opt-in: an ISO range, both ends inclusive, drawn as a tint across its
     * days — the week or period a neighbouring view shows.
     */
    highlightRange?: { start: string; end: string } | null;
  }>(),
  {
    view: 'year',
    days: undefined,
    firstDayOfWeek: 1,
    locale: undefined,
    showWeekNumbers: false,
    selectable: false,
    showHeader: true,
    compact: false,
    selectedDate: null,
    highlightRange: null,
  }
);

const emit = defineEmits<{
  /** Only while `selectable` — see the prop. */
  dayClick: [date: string];
  'update:focusDate': [date: string];
  'update:view': [view: NxCalendarView];
}>();

const root = ref<HTMLElement | null>(null);

/**
 * `view` and `focusDate` are both props a consumer MAY bind. The header writes
 * to these mirrors and emits, so the component works on its own and stays
 * controlled when the consumer does bind — a plain emit would leave the switch
 * dead for everyone who does not.
 */
const currentView = ref(props.view);
watch(
  () => props.view,
  (value) => (currentView.value = value)
);

/**
 * Everything downstream trusts this to be a real date: a malformed value would
 * reach `new Date(NaN, …)` and render a month of Invalid Dates, with the month
 * name and every accessible name to match. Today keeps the component usable,
 * and the warning keeps the consumer's bug visible instead of quietly showing
 * the wrong month.
 */
function resolveFocus(value: string): string {
  if (isIsoDate(value)) return value;
  if (import.meta.env.DEV) {
    console.warn(
      `[NxCalendar] focusDate must be an ISO date (YYYY-MM-DD); got "${value}". Showing today.`
    );
  }
  return todayIso();
}

/** The cell that holds `tabindex="0"`. Follows `focusDate` and the arrow keys. */
const activeDate = ref(resolveFocus(props.focusDate));
watch(
  () => props.focusDate,
  (value) => (activeDate.value = resolveFocus(value))
);

/**
 * The cursor is a visible period, not private state: paging with PageDown or
 * arrowing off the last row reveals another month, and a consumer bound to
 * `focus-date` has to learn about it — that binding is what tells it which
 * period to load its days for. Emitting also keeps `step()` honest, which counts
 * from `activeDate`: unemitted, the next header click would move the bound value
 * by two months, one of which the consumer never saw.
 */
async function onFocusDate(date: string): Promise<void> {
  activeDate.value = date;
  emit('update:focusDate', date);
  await nextTick();
  root.value?.querySelector<HTMLElement>(`[data-date="${date}"][tabindex="0"]`)?.focus();
}

const i18n = inject(i18nInjectionKey, null);

const resolvedLocale = computed(
  () => props.locale ?? (i18n?.locale?.value as string | undefined) ?? 'de-DE'
);

/**
 * The rendered block follows the keyboard cursor: paging with PageDown or
 * arrowing off the last row has to reveal the month it lands in, otherwise the
 * focused day would have no cell to sit in. The cursor is emitted as it moves,
 * so a bound prop and the block on screen name the same period. Without
 * `selectable` the cursor never moves on its own.
 */
const focus = computed(() => {
  const { year, month } = parseIso(activeDate.value);
  return { year, month };
});

const dayMap = computed(() => indexDays(props.days));

/**
 * How the view renders a month — one decision, so it travels as one prop
 * instead of four the parent has to keep consistent by hand.
 */
const density = computed<MonthDensity>(() =>
  currentView.value === 'year'
    ? {
        maxMarkers: 3,
        showAdjacentDays: false,
        // Two letters, same as the month view: a single 'M' cannot be told from
        // the other 'M', and the capped table has the width for 'Mo'.
        weekdayFormat: 'short',
        compact: true,
        stretch: false,
      }
    : props.compact
      ? {
          maxMarkers: 3,
          showAdjacentDays: true,
          weekdayFormat: 'short',
          compact: true,
          stretch: false,
          mini: true,
        }
      : {
          // Three either way for now: the month cell has room for a fourth band,
          // but a day that means four things at once is a day nobody reads off a
          // grid. What does not fit is named in the tooltip instead of drawn.
          maxMarkers: 3,
          showAdjacentDays: true,
          weekdayFormat: 'short',
          compact: false,
          stretch: true,
        }
);

/**
 * The kit must never print a raw translation key, so every label carries an
 * English fallback for a host app that has not got the entry yet.
 */
function label(key: string, fallback: string): string {
  const translated = i18n?.t(key);
  return !translated || translated === key ? fallback : translated;
}

const navLabels = computed(() =>
  currentView.value === 'year'
    ? {
        prev: label('common.calendar.previous_year', 'Previous year'),
        next: label('common.calendar.next_year', 'Next year'),
      }
    : {
        prev: label('common.calendar.previous_month', 'Previous month'),
        next: label('common.calendar.next_month', 'Next month'),
      }
);

/** The year alone in the year view; month and year in the month view. */
const periodLabel = computed(() => {
  const date = new Date(focus.value.year, focus.value.month - 1, 1);
  const options: Intl.DateTimeFormatOptions =
    currentView.value === 'year' ? { year: 'numeric' } : { month: 'long', year: 'numeric' };
  return new Intl.DateTimeFormat(resolvedLocale.value, options).format(date);
});

/**
 * One whole period per click: a year in the year view, a month in the month
 * view — so December steps into January of the next year. The step is emitted
 * as well, so a consumer can bind `v-model:focus-date` and keep the value.
 */
function step(direction: -1 | 1): void {
  const next = shiftMonths(activeDate.value, direction * (currentView.value === 'year' ? 12 : 1));
  activeDate.value = next;
  emit('update:focusDate', next);
}

const todayLabel = computed(() => label('common.calendar.today', 'Today'));

/** German ERPs read "KW 6"; the kit ships the neutral abbreviation. */
const weekPrefix = computed(() => label('common.calendar.week_prefix', 'Wk'));

const viewOptions = computed(() => [
  { value: 'year' as const, label: label('common.calendar.view_year', 'Year') },
  { value: 'month' as const, label: label('common.calendar.view_month', 'Month') },
]);

/** Today, as the local calendar sees it — never UTC, or the day flips at 01:00. */
function todayIso(): string {
  const now = new Date();
  return toIso(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/**
 * Today is state, not a constant: a calendar left open overnight would keep
 * ringing yesterday, and in the year view every month block used to work it out
 * for itself. One value, one timer — rescheduled at each local midnight instead
 * of polling, and a second past it so the clock has certainly turned over.
 */
const today = ref(todayIso());
let midnight: ReturnType<typeof setTimeout> | undefined;

function scheduleMidnight(): void {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  midnight = setTimeout(
    () => {
      today.value = todayIso();
      scheduleMidnight();
    },
    next.getTime() - now.getTime() + 1000
  );
}

onMounted(scheduleMidnight);
onBeforeUnmount(() => clearTimeout(midnight));

/**
 * Jumps the cursor onto today's cell: the view follows the cursor, so the year
 * view reveals this year and the month view this month. Focus stays on the
 * button — the cell becomes the grid's tab stop, so Tab walks straight into it.
 */
function goToday(): void {
  activeDate.value = today.value;
  emit('update:focusDate', today.value);
}

function setView(next: NxCalendarView): void {
  currentView.value = next;
  emit('update:view', next);
}

const months = computed(() => {
  const nameFormatter = new Intl.DateTimeFormat(resolvedLocale.value, { month: 'long' });
  // Each month is its own `role="grid"` and needs a name that stands on its own:
  // the visible heading is hidden in the month view, and twelve grids called
  // "Januar" would not say which year they belong to.
  const labelFormatter = new Intl.DateTimeFormat(resolvedLocale.value, {
    month: 'long',
    year: 'numeric',
  });
  const list =
    currentView.value === 'year'
      ? Array.from({ length: 12 }, (_, i) => i + 1)
      : [focus.value.month];
  return list.map((month) => {
    const date = new Date(focus.value.year, month - 1, 1);
    return { month, name: nameFormatter.format(date), label: labelFormatter.format(date) };
  });
});
</script>

<template>
  <div ref="root" class="nx-calendar-root">
    <div v-if="showHeader" class="nx-calendar-header">
      <div class="nx-calendar-nav">
        <NxButton
          :aria-label="navLabels.prev"
          height="s"
          variant="ghost"
          width="icon-only"
          @click="step(-1)"
        >
          <ChevronLeftIcon filled />
        </NxButton>
        <span class="nx-calendar-nav-label">{{ periodLabel }}</span>
        <NxButton
          :aria-label="navLabels.next"
          height="s"
          variant="ghost"
          width="icon-only"
          @click="step(1)"
        >
          <ChevronRightIcon filled />
        </NxButton>
      </div>

      <NxButton class="nx-calendar-today" height="s" variant="outline" @click="goToday">
        {{ todayLabel }}
      </NxButton>

      <!-- Whatever the consumer wants next to the navigation — its own switches,
           filters, a legend. The kit reserves no meaning for this zone. -->
      <slot name="controls" />

      <NxToggleGroup
        class="nx-calendar-view-switch"
        height="s"
        :model-value="currentView"
        :options="viewOptions"
        @update:model-value="setView"
      />
    </div>

    <div :class="['nx-calendar', `view-${currentView}`, { 'is-mini': density.mini }]">
      <section v-for="block in months" :key="block.month" class="nx-calendar-block">
        <h3 v-if="currentView === 'year' || !showHeader" class="nx-calendar-month-name">
          {{ currentView === 'month' && compact ? block.label : block.name }}
        </h3>
        <NxCalendarMonthGrid
          :active-date="activeDate"
          :day-map="dayMap"
          :density="density"
          :first-day-of-week="firstDayOfWeek"
          :highlight-range="highlightRange"
          :locale="resolvedLocale"
          :month="block.month"
          :month-label="block.label"
          :selectable="selectable"
          :selected-date="selectedDate"
          :today="today"
          :show-week-numbers="currentView === 'month' && showWeekNumbers"
          :week-prefix="weekPrefix"
          :year="focus.year"
          @day-click="emit('dayClick', $event)"
          @focus-date="onFocusDate"
        >
          <template v-if="$slots.day" #day="slotProps">
            <slot name="day" v-bind="slotProps" />
          </template>
        </NxCalendarMonthGrid>
      </section>
    </div>
  </div>
</template>

<style scoped>
.nx-calendar {
  display: grid;
  gap: var(--nxt-space-sm);
}

/* Only the month view claims the leftover height; the year view keeps its own,
   which is the sum of its block rows. This is also the SCROLLER for the grid's
   floor (I-1515): it may shrink to nothing (`min-height: 0`), while the grid
   inside it may not go under its floor — so a box smaller than the floor scrolls
   here instead of letting the calendar run out over whatever follows. The floor
   has to sit on the grid, i.e. on this scroller's CONTENT: on the scroller or on
   the root it would be the scroller itself that cannot shrink, and then nothing
   ever scrolls. Boxes at or above the floor never scroll — the `1fr` rows fill
   them. */
.nx-calendar.view-month {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.nx-calendar.view-month .nx-calendar-block {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* The header sits above the grids — a control between the rows would break a
   grid's structure for assistive tech. Navigation and today on the left, the
   consumer's controls next, the view switch pushed right. */
.nx-calendar-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--nxt-space-sm);
  margin-bottom: var(--nxt-space-sm);
}

.nx-calendar-nav {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
}

.nx-calendar-view-switch {
  margin-inline-start: auto;
}

/* Reserved width so the buttons hold still while the label changes: 144px
   against the longest German label, "September 2026" at 110px. A locale with
   longer month names can still push them — measure before widening. */
.nx-calendar-nav-label {
  min-width: calc(var(--nxt-size-unit) * 9);
  color: var(--nxt-fg);
  font-size: var(--nxt-table-size);
  font-weight: var(--nxt-font-weight-semibold);
  text-align: center;
}

/* The column count follows the CONTAINER, never the viewport: this sits next to
   the ERP's sidebar, so the width the calendar actually gets is what may decide
   — a media query would count pixels it never has. */
/* Header above, grid below — and the whole thing hands its height down, so a
   consumer sizes the month view by sizing the box around it. */
.nx-calendar-root {
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

/* One month per row is the floor, and the grid is capped at six tables rather
   than stretched: a block pulled across a 2K screen turns its rows flat again.
   12 divides by 6, 4, 3, 2 and 1, so every step fills its last row exactly. */
.nx-calendar.view-year {
  grid-template-columns: minmax(0, 1fr);
  justify-items: center;
  max-width: calc(var(--nxt-size-unit) * 123);
  column-gap: calc(var(--nxt-space-sm) * 1.5);
  row-gap: var(--nxt-space-lg);
}

/* Each step needs room for one more table plus its gap — 312px per table, 18px
   between them, so six ask for 1962px. A container query condition cannot read a
   custom property, so these are the only raw lengths in the component; widen a
   gap and they have to move with it. */
@container (min-width: 41rem) {
  .nx-calendar.view-year {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@container (min-width: 61rem) {
  .nx-calendar.view-year {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@container (min-width: 82rem) {
  .nx-calendar.view-year {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@container (min-width: 123rem) {
  .nx-calendar.view-year {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}

/* One month table, half a month wider than the grid strictly needs — seven
   columns of roughly 43px against a 32px row. Deliberately below the 44x44 hit
   target of the guidelines: twelve tables of 44px cells ask for more than twice
   the width a year view ever gets, and an overview that only fits on a 4K screen
   is no overview. The month view, where a day is actually worked with, is well
   over it. */
.nx-calendar.view-year .nx-calendar-block {
  width: 100%;
  max-width: calc(var(--nxt-size-unit) * 19.5);
}

/* Centred above its table, the way a month header reads in a year grid. The
   month view keeps it left-aligned: its block spans the full width. */
.nx-calendar.view-year .nx-calendar-month-name {
  text-align: center;
}

.nx-calendar-month-name {
  margin: 0 0 var(--nxt-space-xs);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
}

/* The sidebar month's heading sits over round day buttons, not over a framed
   table: in line with the first column's number, a touch stronger. */
.nx-calendar.view-month.is-mini .nx-calendar-month-name {
  margin-block-end: var(--nxt-space-2xs);
  padding-inline: var(--nxt-space-xs);
  font-weight: var(--nxt-font-weight-semibold);
}
</style>
